import { useState } from "react";
import styled, { keyframes } from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import MainContainer from "../../components/MainContainer";
import {
  selectDeleteAccountPending,
  selectUser,
} from "../../store/slices/user/slice";
import ConfirmationModal from "../../components/ConfirmationModal/ConfirmationModal";
import { toast } from "sonner";
import { deleteAccount } from "../../store/slices/user/asyncThunk";
import { ReactComponent as TrashSvg } from "../../assets/icons/trash.svg";
import { ReactComponent as UserSvg } from "../../assets/icons/user.svg";
import { ReactComponent as CopySvg } from "../../assets/icons/copy.svg";
import { ReactComponent as ChevronSvg } from "../../assets/icons/chevronLeft.svg";

const DeleteAccountPage = () => {
  const [confirmEmail, setConfirmEmail] = useState("");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const user = useSelector(selectUser);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isLoading = useSelector(selectDeleteAccountPending);

  const handleDeleteAccount = (e) => {
    e.preventDefault();
    if (confirmEmail !== user?.username) {
      toast.error("Username does not match. Please try again.");
      return;
    }
    setShowConfirmModal(true);
  };

  const handleConfirmDelete = () => {
    setShowConfirmModal(false);
    dispatch(deleteAccount({ username: confirmEmail }))
      .unwrap()
      .then(() => {
        toast.success(
          "Your account is scheduled for deletion in 7 days. All drops, videos, and personal data will be erased.",
        );
        navigate("/");
      })
      .catch(() => {
        toast.error(
          "An error occurred while deleting your account. Please try again.",
        );
      });
  };

  const handleCopy = () => {
    if (!user?.username) return;
    navigator.clipboard
      .writeText(user.username)
      .then(() => toast.success("Username copied to clipboard"))
      .catch(() => toast.error("Could not copy username"));
  };

  const handleBack = () => {
    navigate(-1);
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  };

  const matches = confirmEmail.length > 0 && confirmEmail === user?.username;

  return (
    <MainContainer>
      <Container>
        <PageHeader>
          <Title>Delete account</Title>
          <BackButton onClick={handleBack} type="button">
            <ChevronSvg />
            Back
          </BackButton>
        </PageHeader>

        <HeroCard>
          <HeroIcon>
            <TrashSvg />
          </HeroIcon>
          <HeroBody>
            <HeroTitle>This action is permanent</HeroTitle>
            <HeroSub>
              Your drops, generated videos, and personal data will be
              irreversibly erased. You can create a new account later — but
              <strong> free drops are issued only to first-time users</strong>.
            </HeroSub>
          </HeroBody>
        </HeroCard>

        <Steps>
          <StepCard>
            <StepHead>
              <StepNumber>1</StepNumber>
              <StepHeadText>
                <StepTitle>Your username</StepTitle>
                <StepHint>Tap the chip on the right to copy it.</StepHint>
              </StepHeadText>
            </StepHead>
            <UsernameRow type="button" onClick={handleCopy}>
              <UsernameIcon>
                <UserSvg />
              </UsernameIcon>
              <UsernameText>{user?.username || "—"}</UsernameText>
              <CopyChip>
                <CopySvg />
                Copy
              </CopyChip>
            </UsernameRow>
          </StepCard>

          <StepCard>
            <StepHead>
              <StepNumber $danger>2</StepNumber>
              <StepHeadText>
                <StepTitle>Confirm to delete</StepTitle>
                <StepHint>
                  Re-type your username exactly as shown above.
                </StepHint>
              </StepHeadText>
            </StepHead>

            <InputField $matches={matches}>
              <InputIcon>
                <UserSvg />
              </InputIcon>
              <Input
                id="confirm-username"
                type="text"
                value={confirmEmail}
                onChange={(e) => setConfirmEmail(e.target.value)}
                placeholder="Type your username"
                autoComplete="off"
                spellCheck={false}
              />
              <MatchPill $matches={matches} aria-live="polite">
                <MatchDot $matches={matches} />
                {matches ? "Match" : "No match"}
              </MatchPill>
            </InputField>

            <ButtonGroup>
              <SecondaryButton type="button" onClick={handleBack}>
                Cancel
              </SecondaryButton>
              <DangerButton
                type="button"
                onClick={handleDeleteAccount}
                disabled={isLoading || !matches}
              >
                <TrashSvg />
                {isLoading ? "Deleting…" : "Delete account"}
              </DangerButton>
            </ButtonGroup>
          </StepCard>
        </Steps>
      </Container>

      <ConfirmationModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Account"
        message="Are you absolutely sure? This action cannot be undone."
        confirmText="Delete Account"
        cancelText="Cancel"
        confirmButtonType="danger"
        isLoading={isLoading}
      />
    </MainContainer>
  );
};

export default DeleteAccountPage;

/* ─── Tokens (page-local) ────────────────────────────────────────────── */
const DANGER_500 = "#e64561";
const DANGER_600 = "#cf2f4c";
const DANGER_RGB = "230, 69, 97";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
`;

/* ─── Layout ─────────────────────────────────────────────────────────── */

const Container = styled.div`
  width: 100%;
  max-width: 760px;
  margin: 0 auto;
  padding: 28px 0 64px;
  display: flex;
  flex-direction: column;
  gap: 22px;
  font-family: var(--font-family-primary);
  color: var(--color-text-body);
  animation: ${fadeIn} 0.42s var(--ease-snappy) both;

  @media (max-width: 768px) {
    padding: 18px 0 48px;
    gap: 16px;
  }
`;

const PageHeader = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding-bottom: 4px;

  @media (max-width: 520px) {
    gap: 10px;
  }
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 16px;
  border-radius: 999px;
  border: 1px solid var(--color-line);
  background: var(--color-surface-2);
  backdrop-filter: blur(16px) saturate(160%);
  -webkit-backdrop-filter: blur(16px) saturate(160%);
  box-shadow: var(--shadow-micro);
  color: var(--color-text-body);
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  outline: none;
  transition:
    transform 200ms var(--ease-snappy),
    border-color 200ms var(--ease-snappy);

  svg {
    width: 14px;
    height: 14px;
    path {
      stroke: var(--color-brand-700);
    }
  }

  &:hover {
    transform: translateY(-1px);
    border-color: var(--color-line-strong);
  }

  [data-theme="dark"] & {
    border-color: transparent;
    box-shadow:
      0 4px 12px rgba(0, 0, 0, 0.45),
      inset 0 1px 0 rgba(255, 255, 255, 0.04);

    svg path {
      stroke: var(--color-brand-400);
    }
  }
`;

const Title = styled.h1`
  margin: 0;
  font-size: 30px;
  font-weight: 800;
  letter-spacing: -0.025em;
  line-height: normal;
  background: linear-gradient(
    0deg,
    var(--color-text-strong) 0%,
    var(--color-text-body) 45%,
    var(--color-brand-600) 100%
  );
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
  filter: drop-shadow(0 2px 4px rgba(var(--color-brand-600-rgb), 0.14))
    drop-shadow(0 1px 2px rgba(var(--glass-white-rgb), 0.7));

  @media (max-width: 768px) {
    font-size: 26px;
  }
`;

/* ─── Hero warning card ──────────────────────────────────────────────── */

const HeroCard = styled.section`
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 18px;
  padding: 22px 24px;
  border-radius: 22px;
  border: 1px solid rgba(${DANGER_RGB}, 0.22);
  background:
    radial-gradient(
      90% 90% at 0% 0%,
      rgba(${DANGER_RGB}, 0.12),
      transparent 60%
    ),
    var(--color-surface-1);
  backdrop-filter: blur(22px) saturate(160%);
  -webkit-backdrop-filter: blur(22px) saturate(160%);
  box-shadow: var(--shadow-panel);
  overflow: hidden;

  [data-theme="dark"] & {
    border-color: rgba(${DANGER_RGB}, 0.32);
    box-shadow:
      0 24px 48px rgba(0, 0, 0, 0.6),
      0 0 0 1px rgba(${DANGER_RGB}, 0.18),
      inset 0 1px 0 rgba(255, 255, 255, 0.04);
  }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    width: 4px;
    background: linear-gradient(180deg, ${DANGER_500} 0%, ${DANGER_600} 100%);
  }

  @media (max-width: 520px) {
    padding: 18px 18px 18px 22px;
    gap: 14px;
  }
`;

const HeroIcon = styled.div`
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 14px;
  background: linear-gradient(
    180deg,
    rgba(${DANGER_RGB}, 0.12) 0%,
    rgba(${DANGER_RGB}, 0.06) 100%
  );
  border: 1px solid rgba(${DANGER_RGB}, 0.28);
  color: ${DANGER_500};
  box-shadow:
    0 8px 18px rgba(${DANGER_RGB}, 0.14),
    inset 0 1px 0 rgba(255, 255, 255, 0.85);

  svg {
    width: 20px;
    height: 20px;
    path {
      stroke: currentColor;
    }
  }

  [data-theme="dark"] & {
    box-shadow:
      0 6px 14px rgba(0, 0, 0, 0.5),
      inset 0 1px 0 rgba(255, 255, 255, 0.05);
  }
`;

const HeroBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const HeroTitle = styled.h2`
  margin: 0;
  font-size: 16px;
  font-weight: 800;
  letter-spacing: -0.01em;
  color: var(--color-text-strong);
`;

const HeroSub = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: normal;
  color: var(--color-text-body);

  strong {
    color: ${DANGER_600};
    font-weight: 700;
  }
`;

/* ─── Step cards ─────────────────────────────────────────────────────── */

const Steps = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const StepCard = styled.section`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 22px 24px 24px;
  border-radius: 22px;
  border: 1px solid var(--color-line);
  background: var(--color-surface-1);
  backdrop-filter: blur(22px) saturate(160%);
  -webkit-backdrop-filter: blur(22px) saturate(160%);
  box-shadow: var(--shadow-panel);

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: 22px;
    background: radial-gradient(
      110% 60% at 50% 0%,
      rgba(var(--color-brand-300-rgb), 0.22) 0%,
      rgba(var(--color-brand-300-rgb), 0) 70%
    );
    pointer-events: none;
  }

  [data-theme="dark"] &::before {
    opacity: 0.35;
  }

  > * {
    position: relative;
  }

  @media (max-width: 520px) {
    padding: 18px 18px 20px;
  }
`;

const StepHead = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const StepNumber = styled.span`
  width: 28px;
  height: 28px;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 800;
  color: #ffffff;
  text-shadow: 0 1px 1px rgba(0, 0, 0, 0.18);
  background: ${({ $danger }) =>
    $danger
      ? `linear-gradient(180deg, ${DANGER_500} 0%, ${DANGER_600} 100%)`
      : "linear-gradient(180deg, var(--color-brand-300) 0%, var(--color-brand-500) 100%)"};
  border: 1px solid rgba(255, 255, 255, 0.55);
  box-shadow: ${({ $danger }) =>
    $danger
      ? `0 4px 10px rgba(${DANGER_RGB}, 0.32), inset 0 1px 0 rgba(255, 255, 255, 0.5)`
      : "0 4px 10px rgba(var(--color-brand-500-rgb), 0.32), inset 0 1px 0 rgba(255, 255, 255, 0.5)"};

  [data-theme="dark"] & {
    border-color: transparent;
  }
`;

const StepHeadText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const StepTitle = styled.h3`
  margin: 0;
  font-size: 15px;
  font-weight: 800;
  letter-spacing: -0.005em;
  color: var(--color-text-strong);
`;

const StepHint = styled.p`
  margin: 0;
  font-size: 12.5px;
  font-weight: 500;
  color: var(--color-text-muted);
`;

/* ─── Username row (Step 1) ──────────────────────────────────────────── */

const UsernameRow = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 12px 12px 14px;
  border-radius: 16px;
  border: 1px solid var(--color-line);
  background: var(--color-surface-2);
  cursor: pointer;
  font: inherit;
  text-align: left;
  outline: none;
  transition:
    transform 180ms var(--ease-snappy),
    border-color 180ms var(--ease-snappy),
    background 180ms var(--ease-snappy);

  &:hover {
    transform: translateY(-1px);
    border-color: var(--color-line-strong);
    background: var(--color-surface-3);
  }

  [data-theme="dark"] & {
    border-color: transparent;
  }
`;

const UsernameIcon = styled.span`
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  color: var(--color-brand-700);
  background: var(--color-brand-mist);
  border: 1px solid var(--color-line);

  svg {
    width: 16px;
    height: 16px;
    path,
    circle {
      stroke: currentColor;
    }
  }

  [data-theme="dark"] & {
    color: var(--color-brand-300);
    background: rgba(var(--color-brand-700-rgb), 0.22);
    border-color: transparent;
  }
`;

const UsernameText = styled.span`
  flex: 1;
  min-width: 0;
  font-size: 15px;
  font-weight: 700;
  color: var(--color-text-strong);
  letter-spacing: -0.01em;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CopyChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 12px;
  border-radius: 999px;
  background: linear-gradient(
    180deg,
    var(--color-brand-300) 0%,
    var(--color-brand-500) 100%
  );
  border: 1px solid rgba(255, 255, 255, 0.55);
  color: #ffffff;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: -0.005em;
  text-shadow: 0 1px 1px rgba(var(--color-brand-900-rgb), 0.22);
  box-shadow: var(--shadow-cta);
  flex-shrink: 0;

  svg {
    width: 14px;
    height: 14px;
    path {
      stroke: currentColor;
    }
  }

  [data-theme="dark"] & {
    border-color: transparent;
  }
`;

/* ─── Confirm input (Step 2) ─────────────────────────────────────────── */

const InputField = styled.label`
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 6px 6px 14px;
  border-radius: 16px;
  border: 1px solid
    ${({ $matches }) =>
      $matches
        ? "rgba(var(--color-brand-400-rgb), 0.45)"
        : "var(--color-line)"};
  background: var(--color-surface-input);
  box-shadow: ${({ $matches }) =>
    $matches
      ? "var(--shadow-glow)"
      : "inset 0 1px 0 rgba(255, 255, 255, 0.95)"};
  transition:
    border-color 200ms var(--ease-snappy),
    box-shadow 200ms var(--ease-snappy);

  &:focus-within {
    border-color: var(--color-line-strong);
    box-shadow: var(--shadow-glow);
  }

  [data-theme="dark"] & {
    box-shadow: ${({ $matches }) =>
      $matches
        ? "var(--shadow-glow)"
        : "inset 0 1px 0 rgba(255, 255, 255, 0.04)"};
  }
`;

const InputIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-muted);

  svg {
    width: 16px;
    height: 16px;
    path,
    circle {
      stroke: currentColor;
    }
  }
`;

const Input = styled.input`
  flex: 1;
  min-width: 0;
  height: 40px;
  padding: 0;
  background: transparent;
  border: none;
  outline: none;
  color: var(--color-text-strong);
  font-family: var(--font-family-primary);
  font-size: 15px;
  font-weight: 600;
  letter-spacing: -0.005em;

  &::placeholder {
    color: var(--color-text-dim);
    font-weight: 500;
  }
`;

const MatchPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 30px;
  padding: 0 12px;
  border-radius: 999px;
  font-size: 11.5px;
  font-weight: 800;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  flex-shrink: 0;
  color: ${({ $matches }) =>
    $matches ? "var(--color-brand-700)" : "var(--color-text-muted)"};
  background: ${({ $matches }) =>
    $matches ? "var(--color-brand-mist)" : "var(--color-surface-2)"};
  border: 1px solid
    ${({ $matches }) =>
      $matches ? "var(--color-line-strong)" : "var(--color-line)"};
  transition:
    color 220ms var(--ease-snappy),
    background 220ms var(--ease-snappy),
    border-color 220ms var(--ease-snappy);

  [data-theme="dark"] & {
    color: ${({ $matches }) =>
      $matches ? "var(--color-brand-300)" : "var(--color-text-muted)"};
    background: ${({ $matches }) =>
      $matches
        ? "rgba(var(--color-brand-700-rgb), 0.22)"
        : "var(--color-surface-2)"};
    border-color: ${({ $matches }) =>
      $matches ? "var(--color-line-strong)" : "transparent"};
  }
`;

const MatchDot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${({ $matches }) =>
    $matches ? "var(--color-brand-500)" : "var(--color-text-dim)"};
  box-shadow: ${({ $matches }) =>
    $matches ? "0 0 10px rgba(var(--color-brand-400-rgb), 0.65)" : "none"};
  transition: background 220ms var(--ease-snappy);
`;

/* ─── Buttons ────────────────────────────────────────────────────────── */

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;

  @media (max-width: 480px) {
    flex-direction: column-reverse;

    > button {
      flex: 0 0 auto;
      width: 100%;
    }
  }
`;

const SecondaryButton = styled.button`
  flex: 1;
  height: 46px;
  padding: 0 22px;
  border-radius: 999px;
  border: 1px solid var(--color-line);
  background: var(--color-surface-2);
  backdrop-filter: blur(16px) saturate(160%);
  -webkit-backdrop-filter: blur(16px) saturate(160%);
  box-shadow: var(--shadow-micro);
  color: var(--color-text-body);
  font-family: var(--font-family-primary);
  font-size: 14px;
  font-weight: 700;
  letter-spacing: -0.005em;
  cursor: pointer;
  outline: none;
  transition:
    transform 200ms var(--ease-snappy),
    border-color 200ms var(--ease-snappy);

  &:hover {
    transform: translateY(-1px);
    border-color: var(--color-line-strong);
  }

  [data-theme="dark"] & {
    border-color: transparent;
    box-shadow:
      0 4px 12px rgba(0, 0, 0, 0.45),
      inset 0 1px 0 rgba(255, 255, 255, 0.04);
  }
`;

const DangerButton = styled.button`
  flex: 1.2;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 46px;
  padding: 0 22px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.55);
  background: linear-gradient(180deg, ${DANGER_500} 0%, ${DANGER_600} 100%);
  color: #ffffff;
  font-family: var(--font-family-primary);
  font-size: 14px;
  font-weight: 800;
  letter-spacing: -0.005em;
  text-shadow: 0 1px 1px rgba(102, 14, 30, 0.35);
  cursor: pointer;
  outline: none;
  box-shadow:
    0 14px 28px -8px rgba(${DANGER_RGB}, 0.42),
    0 4px 10px rgba(${DANGER_RGB}, 0.22),
    inset 0 1px 0 rgba(255, 255, 255, 0.4);
  transition:
    transform 200ms var(--ease-snappy),
    filter 200ms var(--ease-snappy),
    opacity 200ms var(--ease-snappy);

  svg {
    width: 16px;
    height: 16px;
    path {
      stroke: currentColor;
    }
  }

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    filter: brightness(1.05) saturate(1.05);
  }

  &:active:not(:disabled) {
    transform: translateY(0) scale(0.99);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;

    filter: saturate(0.7);
  }

  [data-theme="dark"] & {
    border-color: transparent;
  }
`;
